import React, { useRef, useState, useEffect, useMemo } from 'react';
import { Patient } from 'web/model/Patient';
import { GetPatientsParams, useGetPatients } from 'web/services/api/queries';
import useClickOutsideCallback from '../hooks/useClickOutsideCallback';

interface SearchablePatientSelectorProps {
    patients?: Patient[];
    selectedPatientId?: string;
    onPatientSelect: (patientId: string) => void;
    placeholder?: string;
    disabled?: boolean;
    includeDefault?: boolean;
    defaultOptionText?: string;
    showPatientDetails?: boolean;
    className?: string;
    searchEnabled?: boolean;
    maxResults?: number;
    organizationId?: string;
}

export const SearchablePatientSelector: React.FC<SearchablePatientSelectorProps> = ({
    selectedPatientId = '',
    onPatientSelect,
    placeholder = "Search patients...",
    disabled = false,
    includeDefault = false,
    defaultOptionText = "Use Default Patient",
    showPatientDetails = true,
    className = "",
    searchEnabled = true,
    maxResults = 1000,
    organizationId
}) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
    const [isUserTyping, setIsUserTyping] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    
    // Build query params
    const queryParams = useMemo(() => {
        if (!searchEnabled) return undefined;
        const params: GetPatientsParams = { generalSearch: searchQuery };
        if (organizationId) {
            params.organizationId = organizationId;
        }
        return params;
    }, [organizationId, searchEnabled, searchQuery]);

    // Use external patients if provided, otherwise fetch them
    const { data: fetchedPatientsData, isLoading: isLoadingPatients } = useGetPatients(
        queryParams,
        0,
        maxResults
    );
    
    const allFetched = (fetchedPatientsData?.data || []).filter(p => !p.isDefault);

    const patients = allFetched;
    const isLoadingInternal = ( isLoadingPatients) || (searchQuery && isLoadingPatients);

    // Update selected patient when selectedPatientId changes
    useEffect(() => {
        if (selectedPatientId && patients) {
            const patient = patients.find(p => p.id === selectedPatientId);
            setSelectedPatient(patient || null);
            if (patient && !isUserTyping) {
                setSearchQuery(patient.toDisplayIdentifier());
            }
        } else {
            setSelectedPatient(null);
            if (!selectedPatientId && !isUserTyping) {
                setSearchQuery('');
            }
        }
    }, [selectedPatientId, patients, isUserTyping]);

    // Close dropdown when clicking outside
    useClickOutsideCallback(dropdownRef, () => {
        setIsDropdownOpen(false);
        setIsUserTyping(false);
    });

    const handlePatientSelect = (patientId: string) => {
        onPatientSelect(patientId);
        setIsDropdownOpen(false);
        setIsUserTyping(false);
        
        if (patientId) {
            const patient = patients.find(p => p.id === patientId);
            if (patient) {
                setSearchQuery(patient.toDisplayIdentifier());
                setSelectedPatient(patient);
            }
        } else {
            setSearchQuery('');
            setSelectedPatient(null);
        }
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
        setIsDropdownOpen(true);
        setIsUserTyping(true);
        // Clear selection if user is typing
        if (selectedPatientId && e.target.value !== selectedPatient?.toDisplayIdentifier()) {
            onPatientSelect('');
        }
    };

    const handleInputFocus = () => {
        if (!disabled) {
            setIsDropdownOpen(true);
        }
    };

    return (
        <div className={`relative ${className}`} ref={dropdownRef}>
            <div className="relative">
                <input
                    type="text"
                    placeholder={placeholder}
                    value={searchQuery}
                    onChange={handleSearchChange}
                    onFocus={handleInputFocus}
                    disabled={disabled}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm 
                             focus:ring-2 focus:ring-blue-500 focus:border-transparent 
                             transition-all duration-200 placeholder-gray-400
                             disabled:bg-gray-50 disabled:text-gray-500"
                />
            </div>
            
            {isDropdownOpen && !disabled && (
                <div className="absolute w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg 
                               max-h-60 overflow-y-auto z-50">
                    {includeDefault && (
                        <div 
                            className="p-2 hover:bg-gray-50 cursor-pointer transition-colors duration-150
                                     border-b border-gray-100"
                            onClick={() => handlePatientSelect('')}
                        >
                            {defaultOptionText}
                        </div>
                    )}
                    
                    {isLoadingInternal ? (
                        <div className="p-4 text-center text-gray-500">
                            <div className="inline-block animate-spin rounded-full h-4 w-4 border-2 
                                          border-gray-300 border-t-blue-500 mr-2" />
                            Loading patients...
                        </div>
                    ) : patients.length === 0 ? (
                        <div className="p-4 text-center text-gray-500">
                            No patients found
                        </div>
                    ) : (
                        patients.map((patient) => (
                            <div
                                key={patient.id}
                                className={`p-2 hover:bg-gray-50 cursor-pointer transition-colors duration-150
                                         ${selectedPatientId === patient.id ? 'bg-blue-50' : ''}`}
                                onClick={() => handlePatientSelect(patient.id)}
                            >
                                <div className="font-medium">{patient.toDisplayIdentifier()}</div>
                                {showPatientDetails && patient.breed && (
                                    <div className="text-sm text-gray-500">{patient.breed}</div>
                                )}
                                {showPatientDetails && patient.ownerLastName && (
                                    <div className="text-sm text-gray-500">Owner: {patient.ownerLastName}</div>
                                )}
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
}; 